import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { AttachmentSource, Block } from '../lib/api.ts'
import type { PagesStatus } from '../lib/usePrescriptionPages.ts'
import { trimToNextLine } from '../lib/highlightBox.ts'
import {
  IDENTITY_TRANSFORM,
  MAX_SCALE,
  clampPan,
  clampScale,
  containedImageSize,
  isTapNotDrag,
  maxScaleForImage,
  nextRotation,
  panBy,
  regionBoxToCss,
  regionZoomBox,
  rotatePanDelta,
  rotatedContainerSize,
  scaleToZoomPercent,
  toCssTransform,
  transformForCropBox,
  transformForFitPage,
  unionOfRegions,
  wheelDeltaToScaleFactor,
  zoomAtPoint,
  zoomLevelsFor,
  zoomStepDown,
  zoomStepUp,
  type RotationDegrees,
  type Size,
  type Transform,
} from '../lib/panZoom.ts'
import {
  PointerButton,
  hasPosition,
  normalizedWheelDelta,
  releasedElsewhere,
  startsPan,
} from '../lib/panZoomInput.ts'
import { downloadPages } from '../lib/downloadPages.ts'
import { pagesOfShownDocument } from '../lib/prescriptionDocument.ts'
import { printPages } from '../lib/printPages.ts'
import { Button } from './Button.tsx'
import { icons } from './icons.ts'

/**
 * The prescription, with the cited blocks drawn over it.
 *
 * This is what makes the verification checkable rather than merely assertive: a
 * pharmacist should never have to take our word for where something came from.
 *
 * **It follows the finding.** Stepping onto the prescriber check frames the
 * prescriber's name and draws the block over it. Drawing the box without moving
 * the viewport leaves someone hunting a small rectangle on a page-sized
 * photograph, which is the work the citation was supposed to save them.
 *
 * The chrome is V1's, and so is every behaviour that looks arbitrary: the
 * stepper instead of a dropdown, the rotate button, a click clearing the
 * highlight while a drag does not. Each came from a review round with a
 * pharmacist. The geometry lives in `lib/panZoom.ts` with its reasons.
 */

export interface PrescriptionViewerProps {
  /**
   * The documents to show, each with the page number a block cites.
   *
   * The number is the attachment's index on the quotation, which stopped
   * matching this list's order when receipts started being left out: a receita
   * that is the second attachment is the first thing shown here. Matching a
   * highlight on list position would draw it on the wrong document.
   */
  readonly pageUrls: readonly {
    readonly url: string
    readonly page: number
    /** `manual` when a person attached it on the conferência (#276); marked as such. */
    readonly source?: AttachmentSource | undefined
    /** The attachment the page belongs to, reported by `onShownChange`. */
    readonly attachmentId?: string | undefined
  }[]
  readonly blocks: readonly Block[]
  /** Block refs to highlight and frame. Usually one finding's worth. */
  readonly highlighted: readonly string[]
  /**
   * Which of the highlighted blocks the reader is pointing at right now.
   *
   * Drawn stronger than the rest rather than instead of them: with several
   * rows pinned, replacing the set on hover hid everything else, so glancing
   * at one selection destroyed the view of the others. This says "and that
   * one" without unsaying the first.
   */
  readonly emphasised?: readonly string[] | undefined
  /**
   * Pointing at a block on the page, when nothing is chosen.
   *
   * The reverse of the row hover: reading the prescription and wanting to
   * know what the system made of *that* line. Offered only while the
   * selection is empty -- once something is pinned the page is answering a
   * question already asked, and every stray mouse movement across it would
   * change the answer.
   */
  readonly onHoverBlock?: ((blockRef: string | undefined) => void) | undefined
  /**
   * Clicking a block, which pins its row -- or unpins it if it already is.
   *
   * The page is a second way to say the same thing as the pin column, and it
   * has to work in both directions: pinning from the page and then having to
   * cross to the other pane to undo it is a trap.
   */
  readonly onClickBlock?: ((blockRef: string) => void) | undefined
  /**
   * Which blocks answer to something on the comparison side.
   *
   * Only these are hoverable. A prescription's page is mostly blocks nothing
   * was said about -- a letterhead, an address, a date -- and offering them
   * as targets promises an answer that does not exist.
   */
  readonly hoverable?: readonly string[] | undefined
  /**
   * Whether the reverse hover is live.
   *
   * Told, not inferred. It used to be derived from `highlighted` being empty
   * -- but hovering a block *fills* `highlighted`, so the first block hovered
   * silenced every block after it, including its own mouse-leave. The result
   * was one rectangle stuck on the page for good. Only the holder of the pins
   * knows whether anything is pinned.
   */
  readonly hoverActive?: boolean | undefined
  /**
   * Which highlighted blocks are the diverging ones.
   *
   * `tone` colours the whole selection, which was right when only one thing
   * could be selected. With several pinned at once a clean row and a flagged
   * one were drawn identically -- the comparison side distinguished them and
   * the page did not, so the colours disagreed across the two panes.
   */
  readonly errorRefs?: readonly string[] | undefined
  /**
   * Whether an empty `pageUrls` means no image, or no image *yet*.
   *
   * Omitted by callers that hold the pages themselves; then empty means empty.
   */
  readonly pagesStatus?: PagesStatus | undefined
  /**
   * What the viewport frames, when that is not simply what is highlighted.
   *
   * Pointing at a row should zoom to it -- otherwise its rectangle can be
   * off-screen and hovering appears to do nothing at all. But the pinned
   * rectangles must stay drawn while that happens, so what is *shown* and
   * what is *framed* are two answers, and this is the second one.
   */
  readonly frameOn?: readonly string[] | undefined
  /**
   * Makes the highlighted boxes draggable and resizable, reporting where they
   * end up. Absent, the overlay stays exactly what it was: inert rectangles
   * a pharmacist cannot disturb.
   *
   * Only the highlighted ones move. Dragging any box on the page would mean a
   * label could be edited from a screen whose job is reading, and would make
   * the boxes compete with the pan gesture over every pointer-down.
   */
  readonly onRegionChange?:
    ((blockRef: string, region: { x: number; y: number; w: number; h: number }) => void) | undefined
  /** Red when the finding it belongs to is a discrepancy, blue otherwise. */
  readonly tone?: 'error' | 'brand'
  /**
   * Take the height the parent gives instead of a fixed slice of the window.
   *
   * The viewport is `70vh` by default, which is right on a page that flows:
   * the image gets most of the screen and the rest scrolls past it. On a
   * screen that fills the window it is wrong twice over -- 70vh is more than
   * the panel has left after a header, a rail and a footer, so it overflows,
   * and it is a fixed number where every other panel is sharing what is
   * actually available.
   */
  readonly fill?: boolean | undefined
  /**
   * The attachment on screen, whenever it changes.
   *
   * The panel's header says whether the document being read was attached by
   * a person (#276), and only the viewer knows which one that is.
   */
  readonly onShownChange?: ((attachmentId: string | undefined) => void) | undefined
  /**
   * An attachment to bring on screen: the receita just attached here (#276).
   *
   * Followed until somebody points somewhere else -- a row's citation, or the
   * arrows -- including when its preview is replaced by the pages the run
   * lists, so the file somebody just added is the one they are looking at.
   */
  readonly bringForward?: string | undefined
  /**
   * The stem downloaded pages are named with (`receita-<filial>-<orçamento>`).
   * Only the screen knows which conferência this is; without it, `receita`.
   */
  readonly fileBase?: string | undefined
}

/**
 * What an empty viewer says, by why it is empty.
 *
 * The first was the only line for a while, and it was shown for all three.
 * A pharmacist told the receita had no image -- while the request for it was
 * being cut off -- went looking in the pharmacy's system for a file that was
 * there all along.
 */
const EMPTY_COPY: Record<PagesStatus, { title: string; body: string }> = {
  loading: {
    title: 'Carregando a imagem da receita…',
    body: 'Ela aparece assim que os anexos forem identificados.',
  },
  unavailable: {
    title: 'Não foi possível carregar a imagem da receita.',
    body: 'Tentando de novo. Sem a imagem não é possível conferir a origem dos achados — o restante da conferência continua válido.',
  },
  ready: {
    title: 'Nenhuma imagem da receita disponível.',
    body: 'Sem a imagem não é possível conferir a origem dos achados — o restante da conferência continua válido.',
  },
}

export function PrescriptionViewer({
  pageUrls,
  blocks,
  highlighted,
  emphasised,
  frameOn,
  onHoverBlock,
  onClickBlock,
  hoverActive = false,
  errorRefs,
  pagesStatus = 'ready',
  hoverable,
  onRegionChange,
  tone = 'brand',
  fill = false,
  onShownChange,
  bringForward,
  fileBase,
}: PrescriptionViewerProps) {
  /** The block page number being shown, not its position in the list. */
  const [page, setPage] = useState(0)
  const [transform, setTransform] = useState<Transform>(IDENTITY_TRANSFORM)
  const [rotation, setRotation] = useState<RotationDegrees>(0)
  const [painted, setPainted] = useState<Size | undefined>()
  const [naturalWidth, setNaturalWidth] = useState(0)
  const [loaded, setLoaded] = useState(false)
  const [grabbing, setGrabbing] = useState(false)
  /*
   * A click on the prescription clears the rectangle.
   *
   * She is reading the line the box is drawn over and the box is in the way.
   * It clears the rectangle only -- the zoom she was reading at stays exactly
   * where it is -- and the next finding brings a new one.
   */
  const [dismissed, setDismissed] = useState(false)

  const viewport = useRef<HTMLDivElement>(null)
  const image = useRef<HTMLImageElement>(null)
  const drag = useRef<{ x: number; y: number; travelled: number } | undefined>(undefined)

  /*
   * Drawn is the union; framed is only what was chosen.
   *
   * Hovering adds a rectangle to the page, and if that also counted as a new
   * citation the viewport would re-zoom on every mouse move across the rows.
   * Pointing at something is not choosing it, and the view should not lurch
   * because you looked.
   */
  const active = useMemo(
    () => new Set([...highlighted, ...(emphasised ?? [])]),
    [highlighted, emphasised],
  )
  const framed = useMemo(() => new Set(frameOn ?? highlighted), [frameOn, highlighted])
  const highlightedBlocks = useMemo(
    () => blocks.filter((b) => active.has(b.block_ref)),
    [blocks, active],
  )

  /*
   * The page to be on is the page the finding is on -- the same courtesy as
   * framing it, since a citation on page two is no use to somebody looking at
   * page one. Only when the citation changes, so paging away afterwards sticks.
   */
  const citedPage = highlightedBlocks[0]?.page
  /** The attachment being brought forward, until somebody looks elsewhere. */
  const following = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (citedPage === undefined) return
    following.current = undefined
    setPage(citedPage)
  }, [citedPage])

  useEffect(() => {
    following.current = bringForward
  }, [bringForward])
  useEffect(() => {
    if (following.current === undefined) return
    const first = pageUrls.find((one) => one.attachmentId === following.current)
    if (first !== undefined) setPage(first.page)
  }, [pageUrls, bringForward])

  /** A new citation is a new rectangle, however the last one was dismissed.
      Built from what was framed, so a hover never counts as one. */
  const citation = highlightedBlocks
    .filter((b) => framed.has(b.block_ref))
    .map((b) => b.block_ref)
    .join(',')
  useEffect(() => setDismissed(false), [citation])

  /*
   * The document actually on screen.
   *
   * `page` can name one that is not in the list -- a citation on a receipt
   * that classification later discarded, or a stale highlight -- and falling
   * back to the first is better than a blank viewer with working arrows.
   */
  const shown = useMemo(
    () => pageUrls.find((one) => one.page === page) ?? pageUrls[0],
    [pageUrls, page],
  )
  const at = shown === undefined ? -1 : pageUrls.indexOf(shown)

  const shownAttachment = shown?.attachmentId
  useEffect(() => {
    onShownChange?.(shownAttachment)
  }, [onShownChange, shownAttachment])

  const pageBlocks = useMemo(
    () => highlightedBlocks.filter((b) => b.page === shown?.page),
    [highlightedBlocks, shown],
  )

  /**
   * Everything detected on the page on screen, cited or not.
   *
   * The neighbours a highlight is trimmed against: a rectangle must stop
   * before the next line whether or not that line is part of the same
   * citation.
   */
  const neighbours = useMemo(
    () =>
      blocks
        .filter((b) => b.page === shown?.page)
        .map((b) => ({ x: Number(b.x), y: Number(b.y), w: Number(b.w), h: Number(b.h) })),
    [blocks, shown],
  )

  /*
   * Whether the field is on screen at all.
   *
   * Every page mounts this viewer before its images arrive, so the first
   * render is the empty copy below and there is no field yet. Effects that
   * attach to the field have to wait for it, and they key on this.
   */
  const hasField = pageUrls.length > 0

  /** The measured size the image is painted at, which is not its element's size. */
  const measure = useCallback(() => {
    const box = viewport.current?.getBoundingClientRect()
    const img = image.current
    if (box === undefined || img === null || img.naturalWidth === 0) return

    setNaturalWidth(img.naturalWidth)
    const size = containedImageSize(
      { w: img.naturalWidth, h: img.naturalHeight },
      { w: box.width, h: box.height },
    )
    if (size !== null) setPainted(size)
  }, [])

  useLayoutEffect(() => {
    measure()
    const box = viewport.current
    if (box === null) return undefined
    const observer = new ResizeObserver(measure)
    observer.observe(box)
    return () => observer.disconnect()
  }, [measure, page, hasField])

  const maxScale = useMemo(
    () => (painted === undefined ? MAX_SCALE : maxScaleForImage(naturalWidth, painted.w)),
    [naturalWidth, painted],
  )
  const levels = useMemo(() => zoomLevelsFor(maxScale * 100), [maxScale])

  /** The field in the image's own axes, which turn with it. */
  const fieldSize = useCallback((): Size => {
    const box = viewport.current?.getBoundingClientRect()
    return rotatedContainerSize(rotation, { w: box?.width ?? 0, h: box?.height ?? 0 })
  }, [rotation])

  /*
   * Every gesture goes through here, as a change to the *current* transform.
   *
   * Not to the one this render closed over: a 1000 Hz mouse or a precision
   * touchpad can send several events between two renders, and each computed
   * from the same stale transform overwrote the one before -- the pan lagged
   * behind the cursor and wheel notches were dropped. V1 applied updaters;
   * the port had gone back to values.
   */
  const settle = useCallback(
    (change: (current: Transform) => Transform) => {
      setTransform((current) => {
        const next = change(current)
        return painted === undefined ? next : clampPan(next, painted, fieldSize())
      })
    },
    [painted, fieldSize],
  )

  /*
   * Frame the finding, or open on the page.
   *
   * Keyed on the citation, so it reframes when the step changes and never
   * while the pharmacist is dragging. A finding with no region on this page
   * leaves the viewport alone rather than moving it somewhere arbitrary.
   */
  useEffect(() => {
    if (painted === undefined) return

    const region = unionOfRegions(
      pageBlocks
        .filter((b) => framed.has(b.block_ref))
        .map((b) => ({ x: Number(b.x), y: Number(b.y), w: Number(b.w), h: Number(b.h) })),
    )
    const next =
      region === undefined
        ? transformForFitPage(painted, fieldSize())
        : transformForCropBox(regionZoomBox(region), painted, maxScale, fieldSize())

    setTransform(clampPan(next, painted, fieldSize()))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [citation, painted, maxScale, page])

  /*
   * Wheel zoom, on a listener of our own.
   *
   * React registers `onWheel` as **passive**, where `preventDefault` is
   * ignored and warns. Left as a React handler this zoomed the prescription
   * *and* scrolled the page underneath it -- so a pharmacist pulling in on a
   * dose watched the whole screen slide away from them.
   *
   * The handler is held in a ref rather than re-bound: it closes over the
   * measurements, and re-adding a listener on every change would drop notches
   * mid-gesture.
   *
   * It is attached when the *field* appears, not when the viewer mounts
   * (#396). Every page mounts the viewer before its images arrive, and a
   * listener keyed on mount looked for a field that did not exist yet and
   * never looked again -- so on most loads the wheel was not ours at all. It
   * scrolled the panel instead of zooming, and Ctrl+wheel, the Windows habit
   * for zooming, zoomed the whole browser page. Whether the images or the
   * verification answered first decided which, so it worked on one machine
   * and not on the next.
   *
   * `preventDefault` comes first, before anything that can bail out: a notch
   * that arrives before the image is measured must still not scroll the page.
   */
  const onWheel = useRef<(event: WheelEvent) => void>(() => undefined)
  onWheel.current = (event: WheelEvent) => {
    event.preventDefault()
    if (painted === undefined) return
    const box = viewport.current?.getBoundingClientRect()
    if (box === undefined) return

    const factor = wheelDeltaToScaleFactor(
      normalizedWheelDelta(event.deltaY, event.deltaMode, box.height),
    )
    const [fx, fy] = [event.clientX - box.left, event.clientY - box.top]
    // Anchored on the cursor: zooming into what you are looking at, rather
    // than into the middle and then hunting for it again.
    settle((current) => zoomAtPoint(current, factor, fx, fy, maxScale))
  }

  useEffect(() => {
    const field = viewport.current
    if (field === null) return undefined
    const handler = (event: WheelEvent) => onWheel.current(event)
    field.addEventListener('wheel', handler, { passive: false })
    return () => field.removeEventListener('wheel', handler)
  }, [hasField])

  /**
   * Dragging a region, in the image's coordinates rather than the screen's.
   *
   * A pointer delta is in screen pixels; a region is a fraction of the page.
   * The conversion is the painted size times the zoom -- and the rotation has
   * to be undone first, because on a sideways page dragging right moves the
   * box down. The same transform the pan already does, reused rather than
   * re-derived, so the two gestures cannot disagree about which way is up.
   *
   * `stopPropagation` matters: without it a drag on a box also pans the page
   * beneath it, and the box appears to move at double speed away from the
   * pointer.
   */
  const regionDrag = useRef<
    | {
        ref: string
        mode: 'move' | 'resize'
        start: { x: number; y: number }
        origin: { x: number; y: number; w: number; h: number }
      }
    | undefined
  >(undefined)

  function startRegionDrag(
    event: React.PointerEvent,
    ref: string,
    origin: { x: number; y: number; w: number; h: number },
    mode: 'move' | 'resize',
  ) {
    event.stopPropagation()
    event.preventDefault()
    regionDrag.current = { ref, mode, start: { x: event.clientX, y: event.clientY }, origin }
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  function moveRegion(event: React.PointerEvent): boolean {
    const active = regionDrag.current
    if (active === undefined || painted === undefined || onRegionChange === undefined) return false
    event.stopPropagation()

    const turned = rotatePanDelta(
      rotation,
      event.clientX - active.start.x,
      event.clientY - active.start.y,
    )
    const dx = turned.dx / (painted.w * transform.scale)
    const dy = turned.dy / (painted.h * transform.scale)
    const { x, y, w, h } = active.origin

    // Clamped to the page, and never inverted: a negative width is a box the
    // scorer cannot interpret and the overlay draws inside out.
    const next =
      active.mode === 'move'
        ? {
            x: Math.min(1 - w, Math.max(0, x + dx)),
            y: Math.min(1 - h, Math.max(0, y + dy)),
            w,
            h,
          }
        : {
            x,
            y,
            w: Math.min(1 - x, Math.max(0.005, w + dx)),
            h: Math.min(1 - y, Math.max(0.005, h + dy)),
          }

    onRegionChange(active.ref, next)
    return true
  }

  function endRegionDrag(): boolean {
    if (regionDrag.current === undefined) return false
    regionDrag.current = undefined
    return true
  }

  /*
   * The press that starts a pan.
   *
   * `preventDefault` keeps the gesture ours. Without it Chrome on Windows can
   * turn a press-and-move on the page into its own drag -- selecting text, or
   * dragging a ghost of the image -- and then cancels our pointer sequence
   * halfway, so the pan stops dead or jumps. V1 had this line and the port
   * lost it. The click that follows a still press is unaffected.
   */
  function onPointerDown(event: React.PointerEvent) {
    if (!startsPan(event.button)) return
    event.preventDefault()
    drag.current = { x: event.clientX, y: event.clientY, travelled: 0 }
    setGrabbing(true)
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  /** A pan whose release never reached us: stop following the cursor. */
  function abandonPan() {
    drag.current = undefined
    setGrabbing(false)
  }

  function onPointerMove(event: React.PointerEvent) {
    const live = drag.current !== undefined || regionDrag.current !== undefined
    if (live && releasedElsewhere(event.buttons)) {
      endRegionDrag()
      abandonPan()
      return
    }
    if (!hasPosition(event.clientX, event.clientY)) return
    if (moveRegion(event)) return
    const from = drag.current
    if (from === undefined) return

    const dx = event.clientX - from.x
    const dy = event.clientY - from.y
    drag.current = {
      x: event.clientX,
      y: event.clientY,
      travelled: from.travelled + Math.abs(dx) + Math.abs(dy),
    }

    // The gesture is in screen axes; the pan is in the image's, which turn
    // with it. Without this, dragging right on a sideways page moves it down.
    const turned = rotatePanDelta(rotation, dx, dy)
    settle((current) => panBy(current, turned.dx, turned.dy))
  }

  function onPointerUp(event: React.PointerEvent) {
    if (endRegionDrag()) return
    const from = drag.current
    drag.current = undefined
    setGrabbing(false)
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }

    // A drag that happens to end on the image is not a click. Without the
    // threshold every pan would clear the highlight mid-read.
    /*
     * A tap clears the highlight so the page can be read underneath it --
     * except while the boxes are editable, where it is the opposite of what
     * the gesture means. Clicking the page beside a box you are positioning
     * made the box disappear, which reads as having deleted it.
     */
    if (onRegionChange === undefined && from !== undefined && isTapNotDrag(from.travelled)) {
      setDismissed(true)
    }
  }

  function stepZoom(to: number) {
    if (painted === undefined) return
    const scale = clampScale(to / 100, 0.5, maxScale)
    const field = fieldSize()
    // About the centre of the field, so a step does not throw away where
    // somebody had scrolled to.
    settle((current) =>
      zoomAtPoint(current, scale / current.scale, field.w / 2, field.h / 2, maxScale),
    )
  }

  function rotate() {
    // Starting over is less disorienting than keeping a pan that no longer
    // corresponds to anything on screen.
    setRotation(nextRotation(rotation))
    setTransform(IDENTITY_TRANSFORM)
  }

  if (pageUrls.length === 0) {
    const copy = EMPTY_COPY[pagesStatus]
    return (
      <div className="rounded-base border-stroke-weak bg-background-raised flex flex-col gap-1 border p-6">
        <p className="text-tP text-text-strong">{copy.title}</p>
        <p className="text-tP text-text-weak">{copy.body}</p>
      </div>
    )
  }

  const percent = scaleToZoomPercent(transform.scale)
  // Printing and downloading take the whole document on screen, not the one
  // page of it: the arrows step by page, a receita is kept by file (#494).
  const documentUrls = pagesOfShownDocument(pageUrls, shown).map((one) => one.url)
  const manual = shown?.source === 'manual'
  const showOverlay = loaded && !dismissed && pageBlocks.length > 0

  return (
    <div className={`flex flex-col gap-3 ${fill ? 'h-full min-h-0' : ''}`}>
      {/* V1's bar: the document arrows and their counter on the left, rotate
          and the zoom stepper on the right. The percentage dropdown died in
          review round 2 -- it is a stepper. */}
      <div className="flex flex-wrap items-center gap-3">
        <div data-doc-nav className="flex items-center gap-2">
          <Button
            size="icon"
            variant="tertiary"
            tone="neutral"
            aria-label="Documento anterior"
            leadingIcon={icons.back}
            disabled={at <= 0}
            onClick={() => {
              following.current = undefined
              setPage(pageUrls[Math.max(0, at - 1)]?.page ?? 0)
            }}
          />
          <span className="text-tP text-text-weak">
            {pageUrls.length === 1
              ? 'Documento único'
              : `Documento ${at + 1} de ${pageUrls.length}`}
          </span>
          <Button
            size="icon"
            variant="tertiary"
            tone="neutral"
            aria-label="Próximo documento"
            leadingIcon={icons.forward}
            disabled={at >= pageUrls.length - 1}
            onClick={() => {
              following.current = undefined
              setPage(pageUrls[Math.min(pageUrls.length - 1, at + 1)]?.page ?? 0)
            }}
          />
        </div>

        <div data-doc-actions className="ml-auto flex items-center gap-2">
          <Button
            size="icon"
            variant="tertiary"
            tone="brand"
            aria-label="Imprimir receita"
            title="Imprimir receita"
            leadingIcon={icons.print}
            onClick={() => printPages(documentUrls)}
          />
          <Button
            size="icon"
            variant="tertiary"
            tone="brand"
            aria-label="Baixar receita"
            title="Baixar receita"
            leadingIcon={icons.download}
            onClick={() => void downloadPages(documentUrls, fileBase)}
          />
        </div>

        <div data-zoom-stepper className="flex items-center gap-2">
          <Button
            size="icon"
            variant="tertiary"
            tone="brand"
            aria-label="Girar a imagem"
            leadingIcon={icons.rotate}
            onClick={rotate}
          />
          <Button
            size="icon"
            variant="tertiary"
            tone="brand"
            aria-label="Diminuir o zoom"
            onClick={() => stepZoom(zoomStepDown(percent, levels))}
          >
            −
          </Button>
          <span className="text-tPSemi text-text-brand w-14 text-center tabular-nums">
            {percent}%
          </span>
          <Button
            size="icon"
            variant="tertiary"
            tone="brand"
            aria-label="Aumentar o zoom"
            onClick={() => stepZoom(zoomStepUp(percent, levels))}
          >
            +
          </Button>
        </div>
      </div>

      {/*
        The field, and the transform sits on a wrapper sized to the *painted*
        image so the overlay's percentages land on the prescription rather than
        on the letterboxing beside it.

        Three properties that each answer one Windows failure (#396). The
        comment here promised the first for months while the class list did
        not carry it:
         - `touch-none`: a touchscreen or pen drag pans the prescription
           instead of the browser taking it for a page scroll and cancelling
           our pointer halfway through;
         - `select-none`: a press-and-move starts no text selection, which on
           Windows is also what turns into a native drag;
         - `overflow-clip`, not `overflow-hidden`: a hidden box is still a
           scroll container, and the zoomed image overflows it, so anything
           that scrolled it -- a selection dragged to its edge, a focus --
           slid the picture out from under the transform, and every cursor
           position after that pointed somewhere else. A clipped box cannot
           scroll.
        A ghost-image drag is refused twice: the image is not draggable, and a
        drag that starts anyway is cancelled.
      */}
      <div
        ref={viewport}
        data-viewer-field
        data-manual={manual ? 'true' : undefined}
        className={`rounded-base ${manual ? 'border-stroke-brand-strong' : 'border-stroke-weak'} relative flex touch-none items-center justify-center overflow-clip border select-none ${fill ? 'h-full min-h-0' : 'h-[70vh]'}`}
        style={{ cursor: grabbing ? 'grabbing' : 'grab' }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onLostPointerCapture={abandonPan}
        onDragStart={(event) => event.preventDefault()}
        // Belt and braces for the middle button: whether a prevented
        // pointerdown also stops Windows Chrome's autoscroll is not something
        // every version agrees on, and the mousedown is where it starts.
        onMouseDown={(event) => {
          if (event.button === PointerButton.middle) event.preventDefault()
        }}
      >
        <div style={{ transform: rotation === 0 ? undefined : `rotate(${rotation}deg)` }}>
          <div
            className="relative origin-top-left"
            style={{
              width: painted === undefined ? undefined : `${painted.w}px`,
              height: painted === undefined ? undefined : `${painted.h}px`,
              transform: toCssTransform(transform),
            }}
          >
            <img
              ref={image}
              src={shown?.url}
              alt="Digitalização da receita"
              draggable={false}
              className="block h-full w-full select-none"
              onLoad={() => {
                setLoaded(true)
                measure()
              }}
            />
            {/* Nothing is drawn until the image has loaded: before that there
                is no painted size, and a box positioned against nothing lands
                anywhere. */}
            {/*
              Every block on this page, as a hit area, when nothing is chosen.
              
              Invisible until pointed at: drawing all of them would turn the
              prescription into a grid of boxes, which is the opposite of
              reading it. They sit under the highlight layer so a chosen
              rectangle still takes the pointer first.
            */}
            {/*
              The hit areas stay while something is pinned, because clicking
              one is how it is unpinned. Only the *hover* stops: once a row is
              chosen the page is answering a question already asked, and every
              stray movement across it would change the answer.
            */}
            {loaded && (onHoverBlock !== undefined || onClickBlock !== undefined) && (
              <div className="absolute inset-0">
                {blocks
                  .filter((b) => b.page === page && hoverable?.includes(b.block_ref) === true)
                  .map((block) => (
                    <span
                      key={`hit-${block.block_ref}`}
                      className="hover:bg-fill-brand-weak/25 hover:border-stroke-brand-strong absolute cursor-pointer rounded-[3px] border border-transparent"
                      style={regionBoxToCss({
                        x: Number(block.x),
                        y: Number(block.y),
                        w: Number(block.w),
                        h: Number(block.h),
                      })}
                      onMouseEnter={() =>
                        hoverActive ? onHoverBlock?.(block.block_ref) : undefined
                      }
                      onMouseLeave={() => (hoverActive ? onHoverBlock?.(undefined) : undefined)}
                      /*
                       * The pan must not capture this pointer.
                       *
                       * The viewport calls `setPointerCapture` on pointerdown,
                       * which retargets everything that follows to itself --
                       * so the click never reached the block and clicking a
                       * rectangle did nothing at all. Stopping the pointerdown
                       * keeps the gesture local; stopping the click keeps it
                       * from also nudging the page.
                       */
                      onPointerDown={(event) => event.stopPropagation()}
                      onClick={(event) => {
                        event.stopPropagation()
                        onClickBlock?.(block.block_ref)
                      }}
                    />
                  ))}
              </div>
            )}

            {showOverlay && (
              <div className="pointer-events-none absolute inset-0">
                {pageBlocks.map((block) => {
                  const detected = {
                    x: Number(block.x),
                    y: Number(block.y),
                    w: Number(block.w),
                    h: Number(block.h),
                  }
                  const editable = onRegionChange !== undefined
                  /*
                   * Painted short of the line below, except while editing.
                   *
                   * Somebody dragging a region has to see and grab the box the
                   * detector actually produced -- that is the thing they are
                   * changing. Everybody else is being pointed at a line, and a
                   * rectangle that also covers the next one points at the
                   * wrong words.
                   */
                  const region = editable ? detected : trimToNextLine(detected, neighbours)
                  return (
                    <span
                      key={block.block_ref}
                      title={block.text}
                      className={`absolute rounded-[4px] ${
                        /*
                         * The pointed-at block is filled harder and outlined
                         * thicker; the rest keep the wash they had. A reader
                         * with four rectangles on the page needs to know which
                         * one their pointer is about without losing the other
                         * three.
                         */
                        // Per block, not per selection: a clean row and a
                        // flagged one pinned together must not look alike.
                        (errorRefs?.includes(block.block_ref) ?? tone === 'error')
                          ? emphasised?.includes(block.block_ref) === true
                            ? 'border-[3px] border-stroke-error-strong bg-fill-error-weak/60'
                            : 'border-2 border-stroke-error-strong bg-fill-error-weak/25'
                          : emphasised?.includes(block.block_ref) === true
                            ? 'border-[3px] border-stroke-brand-strong bg-fill-brand-weak/60'
                            : 'border-2 border-stroke-brand-strong bg-fill-brand-weak/25'
                      } ${editable ? 'pointer-events-auto cursor-move' : ''}`}
                      style={regionBoxToCss(region)}
                      onPointerDown={
                        editable
                          ? (event) => startRegionDrag(event, block.block_ref, region, 'move')
                          : undefined
                      }
                    >
                      {editable && (
                        /*
                          One handle, bottom-right, for size. Two gestures on
                          one rectangle is all this needs: drag the body to
                          move it, drag the corner to resize. Eight handles
                          would be a nicer editor and a worse target on the
                          scale these boxes are actually drawn at.
                        */
                        <span
                          role="quantity"
                          onPointerDown={(event) =>
                            startRegionDrag(event, block.block_ref, region, 'resize')
                          }
                          className="border-stroke-brand-strong bg-background-raised absolute -right-1.5 -bottom-1.5 h-3 w-3 cursor-nwse-resize rounded-[2px] border-2"
                        />
                      )}
                    </span>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {highlighted.length > 0 && pageBlocks.length === 0 && (
        /*
          A finding whose blocks are on another page, or whose citation did not
          survive validation. Saying so is the graceful degradation: drawing
          nothing silently looks like a broken highlight, and framing something
          approximate would point at text the finding did not come from.
        */
        <p className="text-tP text-text-weak">Este achado não tem região nesta página.</p>
      )}
    </div>
  )
}
